import { z } from "zod";
import { PublicActor } from "./actor";
import { DistrictId, IsoUtc } from "./primitives";

export const AppEnvironment = z.enum(["local", "test", "staging", "production"]);
export type AppEnvironment = z.infer<typeof AppEnvironment>;

/** Flags the browser may know about. Enforcement always happens server-side. */
export const PublicFeatureFlags = z.object({
  townExplorationEnabled: z.boolean(),
  guestAvatarsEnabled: z.boolean(),
  guestChatEnabled: z.boolean(),
  registrationEnabled: z.boolean(),
  residentialProgressionEnabled: z.boolean(),
  commercialTakeoversEnabled: z.boolean(),
  billboardRentalsEnabled: z.boolean(),
  sponsoredOffersEnabled: z.boolean(),
  /**
   * ADR-0034 addendum: partner-confirmed "sign-up" offers run on this server (off by default; owners then see an earlier one as
   * stopped). The server enforces it; optional so that older fixtures still parse (absent = off).
   */
  partnerOffersEnabled: z.boolean().optional(),
  /** ADR-0014: private messages between verified accounts. */
  privateMessagesEnabled: z.boolean(),
  /** ADR-0016: the four attractions can be entered. */
  venuesEnabled: z.boolean(),
  /** ADR-0016: betting with play chips in the casino and at the race track. */
  venueChanceGamesEnabled: z.boolean(),
  paymentsMode: z.enum(["disabled", "simulated", "stripe_test", "stripe_live"]),
  /**
   * ADR-0047: accounts are for adults (18+): the sign-up form asks for the confirmation and the server refuses a sign-up
   * without it. Optional so that older fixtures still parse (absent = off).
   */
  adultsOnly: z.boolean().optional(),
});
export type PublicFeatureFlags = z.infer<typeof PublicFeatureFlags>;

export const SessionSummary = z.object({
  /** Null when the visitor only has a guest session. */
  userId: z.string().nullable(),
  emailVerified: z.boolean(),
  displayName: z.string().nullable(),
  roles: z.array(z.string()),
});
export type SessionSummary = z.infer<typeof SessionSummary>;

export const BootstrapResponse = z.object({
  environment: AppEnvironment,
  serverTime: IsoUtc,
  worldVersion: z.number().int().min(1),
  /**
   * The district whose square a fresh street join lands on (ADR-0008 reload, extended to districts by ADR-0041). Town v6
   * (ADR-0042): one street room for the whole town; this is the district of the place the visitor was last in: the
   * district of their saved street position (`districtOfPoint`), else of the property whose interior they were last in.
   * Only the district: the visitor lands on its square, never at the saved spot. The browser passes it as the street
   * ticket's `districtId`.
   * ADR-0043 decision 2 (spread arrivals): ABSENT when nothing usable is saved (a new visitor): the browser then asks
   * for the street without a district, the ticket carries no `spawnDistrictId`, and the street room lands the visitor
   * on the least crowded of the arrival squares (Central, Riverside, Hillcrest). Until then the browser assumes the
   * first district.
   */
  startDistrictId: DistrictId.optional(),
  actor: PublicActor,
  session: SessionSummary,
  features: PublicFeatureFlags,
  realtime: z.object({
    /** Same-origin path prefix where the Colyseus endpoint is reachable. */
    endpointPath: z.string(),
    protocolVersion: z.number().int(),
  }),
  limits: z.object({
    chatMaxChars: z.number().int(),
    chatGuestIntervalMs: z.number().int(),
    chatRegisteredIntervalMs: z.number().int(),
    chatGuestDwellSeconds: z.number().int(),
  }),
  /** Short notices the UI must show (e.g. closed beta, test mode). */
  notices: z.array(z.object({ code: z.string(), message: z.string() })),
});
export type BootstrapResponse = z.infer<typeof BootstrapResponse>;

export const HealthResponse = z.object({
  status: z.enum(["ok", "degraded", "down"]),
  service: z.string(),
  version: z.string(),
  environment: AppEnvironment,
  checks: z.record(
    z.string(),
    z.object({ status: z.enum(["ok", "degraded", "down"]), detail: z.string().optional() }),
  ),
  time: IsoUtc,
});
export type HealthResponse = z.infer<typeof HealthResponse>;
